// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@eisinc/XDL/PreStyle/breakpoints";
import systemZIndex from "@eisinc/XDL/PreStyle/zIndex";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => { };

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};

export const getVideoType = (url: string) => {
    const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const vimeoRegex = /(?:vimeo\.com\/)([0-9]+)/;

    if (youtubeRegex.test(url)) {
        return "youtube";
    }
    if (vimeoRegex.test(url)) {
        return "vimeo";
    }
    return "other";
};

/**
 *
 * we have used this equation vw=(fontSize/viewportWidth) × 100 to adjust font size on tablet screen
 * @param mobile
 * @param tablet
 * @param desktop
 * @returns
 */
export const autoClamp = (mobile: number, tablet: number, desktop: number) => {
    const vw = ((tablet / breakpoints.values[2]) * 100).toFixed(2);
    return `clamp(${mobile}px ,${vw}vw , ${desktop}px)`;
};

export const getMyAccountMapLinks = (state: ApplicationState, widgetLinks: LinkModel[]): PageLinkModel[] => {
    return (
        (widgetLinks
            ?.map(widgetLink => {
                const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                if (pageLink) {
                    return { ...pageLink, title: widgetLink.fields.overriddenTitle || pageLink?.title };
                }
                return undefined;
            })
            ?.filter(pageLink => !pageLink?.excludeFromNavigation) as PageLinkModel[]) || []
    );
};

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    values[maxWidths[0]] = {
        slidesPerView: opts?.[0] || 1,
    };
    values[maxWidths[1]] = {
        slidesPerView: opts?.[1] || 2,
    };
    values[maxWidths[2]] = {
        slidesPerView: opts?.[2] || 3,
    };
    values[maxWidths[3]] = {
        slidesPerView: opts?.[3] || 4,
    };
    return values;
};

/**
 * Format a date string as 'Month YYYY', where 'Month' is the abbreviated Month and 'YYYY' is the year.
 * @param {String} dateStr - The date string in 'MM/DD/YYYY' format to be formatted.
 * @returns {String} The formatted date in 'Month YYYY' format.
 */

export const formatMonthYearDate = (dateStr: string): string => {
    const date = new Date(dateStr);

    const options: Intl.DateTimeFormatOptions = {
        month: "short",
        year: "numeric",
    };

    return date.toLocaleDateString("en-US", options);
};

/**
 * Set a CSS variable on the `:root` element dynamically by injecting a `<style>` element.
 * If the new value is the same as the current value, no update is performed to avoid unnecessary DOM changes.
 *
 * @param {string} variableName - The name of the CSS variable to update (e.g., `--header-height`).
 * @param {string} value - The new value to assign to the CSS variable (e.g., `100px`).
 *
 * @example
 * updateCSSVariable('--header-height', '100px');
 */
export const setCSSVariable = (variableName: string, value: string) => {
    if (typeof document === "undefined") {
        return;
    }
    // Get the current value of the CSS variable
    const currentValue = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();

    // Check if the new value is different from the current value
    if (currentValue === value) {
        return;
    }

    // Create or find the <style> element
    let styleElement = document.getElementById("app-styles") as HTMLStyleElement;
    if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = "app-styles";
        document.head.appendChild(styleElement);
    }

    // Update the CSS rule for :root
    styleElement.textContent = `:root { ${variableName}: ${value}; }`;
};
